/**
 * cart-spend-limit.js
 *
 * Storefront layer of the spend cap. Reads the live cap / monthly spend /
 * exemption from the Spend Cap Manager app (/api/check-cap) and HARD-blocks
 * adding products once the cart would exceed the cap — same idea as the
 * one-Pack-per-cart guard: it actively refuses the add and says "no".
 *
 *   1. Add block (main behavior) — a capture-phase guard on both the add
 *      button click AND the product form submit. If the cart (+ prior monthly
 *      spend) is already at/over the cap, OR this add's price would push it
 *      over, the add is cancelled and a message is shown. The button is NOT
 *      disabled (a disabled button fires no click, so it could never explain
 *      itself) — the add is intercepted instead.
 *   2. Checkout block — disables the cart "Checkout" button (with a message)
 *      whenever the cart already exceeds the cap.
 *
 * Subtotal comes from /cart.js (authoritative). The per-item price for the
 * pre-add check is read from the product page's [data-product-price] element.
 *
 * This is the friendly, bypassable layer — the unbypassable backstop is the
 * spend-cap Cart Validation Function. Inspect live state via window.spendCap.
 */
(function () {
  var APP_URL = "https://spend-cap-app-production.up.railway.app";

  // Translated copy comes from window.cartLimitStrings, rendered by
  // layout/theme.liquid from locales/*.json ('cart_limits.*'). The literals
  // passed to msg() are English fallbacks for when that block is missing.
  // [amount] in the string is replaced with the buyer's cap.
  var STRINGS = window.cartLimitStrings || {};
  function msg(key, fallback) {
    return (STRINGS[key] || fallback).replace("[amount]", cap.toFixed(0));
  }
  var MSG_STYLE =
    "margin:10px 0;padding:10px 12px;border-radius:6px;background:#fdecea;color:#611a15;font-size:14px;line-height:1.4;";

  var cap = 0;
  var spent = 0;
  var exempt = false;
  var enabled = true;
  var loaded = false;
  var subtotal = 0; // dollars

  // Expose state for debugging in the browser console.
  window.spendCap = {
    get cap() { return cap; },
    get spent() { return spent; },
    get subtotal() { return subtotal; },
    get exempt() { return exempt; },
    get enabled() { return enabled; },
    get atCap() { return atCap(); },
    refresh: function () { return refreshCart(); },
  };

  function customerEmail() {
    return (window.spendCapCustomer && window.spendCapCustomer.email) || "";
  }

  function loadCap() {
    return fetch(APP_URL + "/api/check-cap?email=" + encodeURIComponent(customerEmail()))
      .then(function (r) { return r.json(); })
      .then(function (d) {
        cap = parseFloat(d.cap || 0);
        spent = parseFloat(d.spent || 0);
        exempt = Boolean(d.exempt);
        enabled = d.spendCapEnabled !== false;
        loaded = true;
        applyCheckoutState();
      })
      .catch(function () {});
  }

  function active() {
    return loaded && enabled && !exempt && cap > 0;
  }

  // At/over the cap -> no more items may be added.
  function atCap() {
    return active() && spent + subtotal >= cap;
  }

  // Strictly over the cap -> the cart is invalid, block checkout.
  function isOver() {
    return active() && spent + subtotal > cap;
  }

  function setSubtotalFromCart(cart) {
    if (cart && typeof cart.items_subtotal_price === "number") {
      subtotal = cart.items_subtotal_price / 100;
    }
  }

  function refreshCart() {
    var base = (window.theme && window.theme.routes && window.theme.routes.cart) || "/cart";
    return fetch(base + ".js")
      .then(function (r) { return r.json(); })
      .then(function (cart) { setSubtotalFromCart(cart); applyCheckoutState(); })
      .catch(function () {});
  }

  function buildMsg(text) {
    var el = document.createElement("div");
    el.setAttribute("data-spend-cap-message", "");
    el.setAttribute("role", "alert");
    el.textContent = text;
    el.style.cssText = MSG_STYLE;
    return el;
  }

  // Parse a money string like "$2,999.00" (USD) into a number of dollars.
  function parseMoney(text) {
    if (!text) return NaN;
    var cleaned = text.replace(/[^0-9.,]/g, "").replace(/,/g, "");
    return parseFloat(cleaned);
  }

  // Best-effort: dollar amount this add would put in the cart (price x qty).
  // NaN if the price can't be read (e.g. a grid quick-add with no price shown).
  function addAmount(form) {
    if (!form) return NaN;
    var qty = 1;
    var qtyInput = form.querySelector('[name="quantity"]');
    if (qtyInput) qty = parseInt(qtyInput.value, 10) || 1;

    var scope = form.closest("[data-product-form-wrapper]") || form;
    var priceEl = scope.querySelector("[data-product-price]") ||
      form.querySelector("[data-product-price]");
    if (!priceEl) return NaN;

    var unit = parseMoney(priceEl.textContent);
    if (isNaN(unit)) return NaN;
    return unit * qty;
  }

  // --- Checkout block ---------------------------------------------------------

  function applyCheckoutState() {
    var over = isOver();
    var buttons = document.querySelectorAll('[name="checkout"]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      btn.disabled = over;
      var holder = btn.closest("form") || btn.parentNode;
      if (!holder) continue;
      var existing = holder.querySelector(":scope > [data-spend-cap-message]");
      if (over && !existing) {
        holder.insertBefore(
          buildMsg(
            msg(
              "spendCartOver",
              "Your cart exceeds your $[amount] spending limit. Please remove items to continue.",
            ),
          ),
          btn,
        );
      } else if (!over && existing) {
        existing.remove();
      }
    }
  }

  // --- Add block (the "no, you can't do that" part) ---------------------------

  // Decide whether an add from this form must be refused; returns a reason
  // string to show, or null to allow.
  function refusalReason(form) {
    if (!active()) return null;
    if (atCap()) {
      return msg(
        "spendAtCap",
        "You've reached your $[amount] spending limit. You can't add more items — please reduce your cart first.",
      );
    }
    var amt = addAmount(form);
    if (!isNaN(amt) && spent + subtotal + amt > cap) {
      return msg(
        "spendWouldExceed",
        "Adding this would put you over your $[amount] spending limit. Please reduce the quantity or remove items first.",
      );
    }
    return null;
  }

  function refuse(e, form, anchorBtn, reason) {
    e.preventDefault();
    e.stopImmediatePropagation();
    var holder =
      (form && form.querySelector("[data-error-display]")) ||
      form ||
      (anchorBtn && anchorBtn.parentNode);
    if (holder && !holder.querySelector("[data-spend-cap-message]")) {
      var msg = buildMsg(reason);
      holder.appendChild(msg);
      setTimeout(function () { if (msg.parentNode) msg.parentNode.removeChild(msg); }, 6000);
    }
  }

  // Catch the click on the add button (capture phase, before the theme handler).
  function onAddClick(e) {
    var target = e.target;
    var btn = target.closest && target.closest("[data-add-to-cart]");
    if (!btn) return;
    var form = btn.closest("[data-product-form]");
    var reason = refusalReason(form);
    if (reason) refuse(e, form, btn, reason);
  }

  // Catch the form submit too (Alpine adds on submit; Enter key bypasses click).
  function onFormSubmit(e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches("[data-product-form]")) return;
    var reason = refusalReason(form);
    if (reason) refuse(e, form, form.querySelector("[data-add-to-cart]"), reason);
  }

  // --- Wiring -----------------------------------------------------------------

  document.addEventListener("click", onAddClick, true);
  document.addEventListener("submit", onFormSubmit, true);
  document.addEventListener("theme:cart:change", function (e) {
    if (e.detail && e.detail.cart) setSubtotalFromCart(e.detail.cart);
    applyCheckoutState();
  });
  document.addEventListener("theme:cart:reload", refreshCart);

  function boot() { loadCap(); refreshCart(); }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
